import PropTypes from 'prop-types';

import {Box, Flex, Heading, Skeleton, Text} from '@salesforce/retail-react-app/app/components/shared/ui';

import {Component} from '@amblique/king-living/app/components/contentful/component';
import {HeroBannerBackgroundVideo} from '@amblique/king-living/app/utils/hero-banner-video';
import {normalizeHex} from '@amblique/king-living/app/utils/normalize-hex';

/**
 * Hero Banner Block (CF-04)
 * Full-viewport hero with background image/video, title overlay, and bottom action bar.
 * - Heading: Title first, then Heading Icon (Image only, no Icon reference) below.
 * - Background: image/video or Background Colour (hex) when no media.
 * - Copy colour: Text Colour (hex) for title/subtitle.
 *
 * @typedef {import('@chakra-ui/styled-system').SystemStyleObject} SystemStyleObject
 * @typedef {"wrapper" | "backgroundWrapper" | "contentWrapper" | "title" | "subtitle" | "bottomBar" | "headingIcon" | "leftCta" | "rightCta"} HeroBannerBlockCF04Parts
 * @typedef {Partial<Record<HeroBannerBlockCF04Parts, SystemStyleObject>>} HeroBannerBlockCF04Styles
 *
 * @param {Object} props - Component props
 * @param {Contentful.HeroBanner} props.component - HeroBanner component data
 * @param {boolean} [props.isComplete] - Whether component has all fields
 * @param {boolean} [props.isFetching] - Whether additional data is being fetched
 * @param {HeroBannerBlockCF04Styles} [props.styles] - Component styles
 * @returns {React.ReactElement}
 */
export const HeroBannerBlockCF04 = ({component, isComplete = true, isFetching = false, styles}) => {
  const {backgroundMedia, backgroundColour, headingImage, headingTitle, subtitle, cta, rightCta} = component;

  // Determine which background to use (Image has priority over Video)
  const backgroundImage = backgroundMedia?.__typename === 'Image' ? backgroundMedia : null;
  const backgroundVideo = backgroundMedia?.__typename === 'Video' ? backgroundMedia : null;
  const hasBackgroundMedia = Boolean(backgroundImage || backgroundVideo);

  // When no background image/video, apply background colour per CF-04 spec
  const fallbackBg = hasBackgroundMedia ? undefined : normalizeHex(backgroundColour);

  return (
    <Box className="hero-banner" {...styles.wrapper}>
      {/* Background Media Layer (image, video, or background colour fallback) */}
      <Box className="hero-image-wrapper" {...styles.backgroundWrapper}>
        {backgroundImage ? (
          <Component component={backgroundImage} fetchpriority="high" loading="eager" {...styles.backgroundMedia} />
        ) : backgroundVideo ? (
          <HeroBannerBackgroundVideo component={backgroundVideo} {...styles.backgroundMedia} />
        ) : fallbackBg ? (
          <Box backgroundColor={fallbackBg} height="100%" width="100%" />
        ) : (
          <Skeleton height="100%" width="100%" />
        )}
      </Box>

      {/* Content Overlay Layer */}
      <Box className="hero-content-wrapper" {...styles.contentWrapper}>
        {!isComplete && isFetching ? (
          <Skeleton height="120px" />
        ) : (
          <>
            {/* CF-04: Heading Title first, then Heading Icon (Image only) below */}
            {headingTitle && <Heading {...styles.title}>{headingTitle}</Heading>}
            {headingImage && (
              <Box {...styles.headingIcon}>
                <Component component={headingImage} />
              </Box>
            )}
            {subtitle && <Text {...styles.subtitle}>{subtitle}</Text>}
          </>
        )}

        {/* Bottom Action Bar */}
        {(cta || rightCta) && (
          <Flex {...styles.bottomBar}>
            {/* Left CTA */}
            {cta && (
              <Box {...styles.leftCta}>
                <Component component={cta} />
              </Box>
            )}

            {/* Right CTA - only shown if rightCta is configured */}
            {rightCta && (
              <Box {...styles.rightCta}>
                <Component component={rightCta} />
              </Box>
            )}
          </Flex>
        )}
      </Box>
    </Box>
  );
};

HeroBannerBlockCF04.displayName = 'HeroBannerBlockCF04';
HeroBannerBlockCF04.propTypes = {
  component: PropTypes.object.isRequired,
  isComplete: PropTypes.bool,
  isFetching: PropTypes.bool,
  styles: PropTypes.object.isRequired,
};
